/**
 * Central language registry. Single source of truth for which languages
 * exist, how they render and whether they are released.
 *
 * status:
 *  - live   : shown in production selectors, sitemap, hreflang, regional defaults
 *  - review : only available in the review environment (preview/staging hosts)
 *  - draft  : never selectable; resources may be incomplete
 *
 * Kill switch: moving a language from 'live' back to 'review' hides it in
 * production. Saved profile preferences are NOT rewritten; affected users
 * simply resolve to English until it is live again.
 *
 * Shared with edge functions via scripts/i18n-sync.ts — do not import
 * browser-only modules here.
 */
export type LanguageCode = 'en' | 'ar' | 'es';
export type LanguageStatus = 'live' | 'review' | 'draft';

export interface LanguageDef {
  code: LanguageCode;
  nativeName: string;
  englishName: string;
  dir: 'ltr' | 'rtl';
  status: LanguageStatus;
  /** BCP-47 tag used for Intl formatting of non-financial text. */
  intlLocale: string;
}

export const SOURCE_LANGUAGE: LanguageCode = 'en';

export const LANGUAGES: Record<LanguageCode, LanguageDef> = {
  en: { code: 'en', nativeName: 'English', englishName: 'English', dir: 'ltr', status: 'live', intlLocale: 'en' },
  ar: { code: 'ar', nativeName: 'العربية', englishName: 'Arabic', dir: 'rtl', status: 'review', intlLocale: 'ar' },
  es: { code: 'es', nativeName: 'Español', englishName: 'Spanish', dir: 'ltr', status: 'review', intlLocale: 'es' },
};

export const ALL_LANGUAGE_CODES = Object.keys(LANGUAGES) as LanguageCode[];

/**
 * Regional defaults: ISO 3166-1 alpha-2 → language. Applied only on an
 * unlocalized first entry, and only when the country signal is reliable.
 * Multilingual markets are listed explicitly rather than inferred.
 */
export const COUNTRY_DEFAULTS: Record<string, LanguageCode> = {
  // GCC
  KW: 'ar', SA: 'ar', AE: 'ar', QA: 'ar', BH: 'ar', OM: 'ar',
  // Spanish-speaking markets
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es',
};

export function isLanguageCode(v: unknown): v is LanguageCode {
  return typeof v === 'string' && (ALL_LANGUAGE_CODES as string[]).includes(v);
}

/** Languages usable in the given environment. */
export function availableLanguages(reviewEnv: boolean): LanguageCode[] {
  return ALL_LANGUAGE_CODES.filter((c) => {
    const s = LANGUAGES[c].status;
    return s === 'live' || (reviewEnv && s === 'review');
  });
}

export function isAvailable(code: unknown, reviewEnv: boolean): code is LanguageCode {
  return isLanguageCode(code) && availableLanguages(reviewEnv).includes(code);
}
